// client/src/api/services/settingsService.ts
import { apiClient } from '../client';
import { ENDPOINTS } from '../endpoints';

// Types
export interface UserProfile {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  location?: string;
  bio?: string;
  profileImage?: string;
  role: string;
  country?: string;
  managedCountry?: string;
}

export interface NotificationPreferences {
  emailNotifications: boolean;
  pushNotifications: boolean;
  smsNotifications?: boolean;
}

export interface TwoFactorStatus {
  enabled: boolean;
  qrCode?: string;
  secret?: string;
}

export interface ConnectedDevice {
  _id: string;
  deviceName: string;
  deviceType: string;
  browser?: string;
  os?: string;
  lastActive: string;
  ipAddress?: string;
  isCurrent?: boolean;
}

export interface ChangePasswordData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// Settings Service
export const settingsService = {
  // Get user profile
  getProfile: async () => {
    try {
      const response = await apiClient.get<{ success: boolean; data: UserProfile }>(
        ENDPOINTS.USER.PROFILE
      );
      return response.data.data;
    } catch (error: unknown) {
      throw error;
    }
  },

  // Update user profile
  updateProfile: async (data: Partial<UserProfile>) => {
    try {
      const response = await apiClient.put(ENDPOINTS.USER.UPDATE_PROFILE, data);
      return response.data;
    } catch (error: unknown) {
      throw error;
    }
  },

  // Upload profile avatar
  uploadAvatar: async (file: File) => {
    try {
      const formData = new FormData();
      formData.append('avatar', file);
      
      const response = await apiClient.post(ENDPOINTS.USER.UPLOAD_AVATAR, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error: unknown) {
      throw error;
    }
  },

  // Change password
  changePassword: async (data: ChangePasswordData) => {
    try {
      const response = await apiClient.post(ENDPOINTS.USER.PASSWORD, data);
      return response.data;
    } catch (error: unknown) {
      throw error;
    }
  },

  // Get notification preferences
  getNotificationPreferences: async () => {
    try {
      const response = await apiClient.get<{ 
        success: boolean; 
        data: NotificationPreferences 
      }>(ENDPOINTS.USER.NOTIFICATIONS);
      return response.data.data;
    } catch (error: unknown) {
      throw error;
    }
  },

  // Update notification preferences
  updateNotificationPreferences: async (preferences: Partial<NotificationPreferences>) => {
    try {
      const response = await apiClient.put(
        ENDPOINTS.USER.UPDATE_NOTIFICATIONS, 
        preferences
      );
      return response.data;
    } catch (error: unknown) {
      throw error;
    }
  },

  // Get 2FA status
  getTwoFactorStatus: async () => {
    try {
      const response = await apiClient.get<{ 
        success: boolean; 
        data: TwoFactorStatus 
      }>(ENDPOINTS.USER.TWO_FA.STATUS);
      return response.data.data;
    } catch (error: unknown) {
      throw error;
    }
  },

  // Enable 2FA
  enableTwoFactor: async (data: { email: string }) => {
    try {
      const response = await apiClient.post<{ 
        success: boolean; 
        data?: { qrCode: string; secret: string };
        qrCode?: string;
        secret?: string;
      }>(ENDPOINTS.USER.TWO_FA.SETUP, data);
      
      // Handle different response formats from backend
      const responseData = response.data.data || response.data;

      return responseData;
    } catch (error: unknown) {
      throw error;
    }
  },

  // Disable 2FA
  disableTwoFactor: async (code?: string | Record<string, unknown>) => {
    try {
      const response = await apiClient.post(ENDPOINTS.USER.TWO_FA.DISABLE, { code: typeof code === 'string' ? code : undefined });
      return response.data;
    } catch (error: unknown) {
      throw error;
    }
  },

  // Verify 2FA code
  verifyTwoFactor: async (email: string, otp: string) => {
    try {
      const response = await apiClient.post(ENDPOINTS.USER.TWO_FA.VERIFY, { email, otp });
      return response.data;
    } catch (error: unknown) {
      throw error;
    }
  },

  // Get connected devices/sessions
  getDevices: async () => {
    try {
      const response = await apiClient.get<{ 
        success: boolean; 
        data: ConnectedDevice[] 
      }>(ENDPOINTS.USER.DEVICES);
      return response.data.data;
    } catch (error: unknown) {
      throw error;
    }
  },

  // Remove device/session
  removeDevice: async (deviceId: string) => {
    try {
      const response = await apiClient.delete(ENDPOINTS.USER.REMOVE_DEVICE(deviceId));
      return response.data;
    } catch (error: unknown) {
      throw error;
    }
  },
};

export default settingsService;
